import * as React from 'react';
import AnalyticsBreakdownChartSection from '../analytics_breakdown_chart/AnalyticsBreakdownChartSection';
import { SpendBySourceStackedBars } from '../charts/SpendBySourceBreakdownChart';
import { fetchSpendReportingsBreakdownComparisonSilent } from '../../thunks/analytics_breakdown_comparison_thunks';
import { SpendSourceDescriptor } from '../../utils/AnalyticsUtils';
import { toBreakdownTotalsRows } from '../../utils/SpendAnalyticsChartUtils';

type SpendBreakdownSectionProps = {
	loading?: boolean;
	filters: any;
	userType: string;
	currency: any;
	// SpendCostsByField rows (one per category) driving the source-stacked single-mode chart.
	rows: any[];
	// Sources rendered as stacked segments (the non-zero subset for the current period).
	activeSources: SpendSourceDescriptor[];
	// Full set the tab totals over (all sources, or invoice-only for Suppliers). Used for the
	// breakdown/comparison totals so a comparison period isn't undercounted when it has spend in a
	// source that's absent from the current period.
	totalSources: SpendSourceDescriptor[];
	// spend_reportings endpoint substring used for the VS-PREV comparison fetch.
	reportKey: string;
	// Page-specific shaping of the raw comparison response into flat SpendCostsByField rows
	// (tree pages flatten + apply local filters; the flat vendors list passes through).
	normalizeComparisonRows: (rawData: any[]) => any[];
	localFilterRevision?: string | number;
	categoryLabel: string;
	titleLabel: string;
	csvTitle: string;
	exportValueLabel: string;
};

// Per-source spend breakdown card shared by the three spend overview tabs (OPE-4875). Renders the
// source-stacked bars in the default (NO-COMPARISON) view and the period comparison in VS-PREV via the
// shared AnalyticsBreakdownChartSection. Centralizes the comparison fetch/normalize + totals wiring so
// the three pages don't each duplicate it.
export const SpendBreakdownSection: React.FC<SpendBreakdownSectionProps> = ({
	loading = false,
	filters,
	userType,
	currency,
	rows,
	activeSources,
	totalSources,
	reportKey,
	normalizeComparisonRows,
	localFilterRevision,
	categoryLabel,
	titleLabel,
	csvTitle,
	exportValueLabel,
}) => {
	const breakdownTotalsRows = toBreakdownTotalsRows(rows, totalSources);
	const breakdownTotal = breakdownTotalsRows.reduce((sum, row) => sum + (row.subTotal || 0), 0);
	const formatValueLabel = (val) =>
		val > 0 ? currency.symbolFormat(val, 0) : currency.symbolFormat(0, 0);

	return (
		<AnalyticsBreakdownChartSection
			loading={loading}
			filters={filters}
			userType={userType}
			primaryChartRows={breakdownTotalsRows}
			fetchComparisonData={(params) =>
				fetchSpendReportingsBreakdownComparisonSilent(reportKey)(userType)(params)
			}
			normalizeComparisonData={(rawData) =>
				toBreakdownTotalsRows(normalizeComparisonRows(rawData), totalSources)
			}
			localFilterRevision={localFilterRevision}
			cols={{ fieldName: { alias: categoryLabel }, subTotal: { alias: 'Spend' } }}
			formatValueLabel={formatValueLabel}
			valueAxisTitle={`Spend (${currency.id})`}
			exportTitle={csvTitle}
			exportCategoryLabel={categoryLabel}
			exportValueLabel={exportValueLabel}
			formatExportValue={(amount) => currency.symbolFormat(amount, 0)}
			chartTitleLabel={titleLabel}
			chartTitleValue={currency.symbolFormat(breakdownTotal, 0)}
			renderSingleModeChart={(chartHeight) => (
				<SpendBySourceStackedBars
					rows={rows}
					activeSources={activeSources}
					currency={currency}
					height={chartHeight}
				/>
			)}
		/>
	);
};
